import type { GetLightInvoicesResponse } from '@weft/api-definition';
import { isNotEmptyArray } from '@weft/utils';
import { getCurrencySymbol } from 'apps/app/src/services/currency-symbol-mapper';
import React, { useEffect } from 'react';
import { LightInvoiceCommitmentCharges } from '../light-invoice-components/LightInvoiceCommitmentCharges';
import { LightInvoiceFixedFeeCharges } from '../light-invoice-components/LightInvoiceFixedFeeCharges';
import { LightInvoiceFooter } from '../light-invoice-components/LightInvoiceFooter';
import { LightInvoiceHeader } from '../light-invoice-components/LightInvoiceHeader';
import { LightInvoicePromotions } from '../light-invoice-components/LightInvoicePromotions';
import { LightInvoiceRevisionedLineItem } from '../light-invoice-components/LightInvoiceRevesionedLineItems';
import { LightInvoiceStatementHeader } from '../light-invoice-components/LightInvoiceStatementHeader';
import { LightInvoiceSummary } from '../light-invoice-components/LightInvoiceSummary';
import { LightInvoiceUsageCharges } from '../light-invoice-components/LightInvoiceUsageCharges';

type LightInvoicesAsPdfListProps = {
  invoices: GetLightInvoicesResponse['invoices'];
  companyName: string;
  onLoad: () => void;
  isStatement?: boolean;
};

export const LightInvoicesAsPdfList: React.FC<LightInvoicesAsPdfListProps> = ({ invoices, companyName, onLoad, isStatement }) => {
  const [isMounted, setIsMounted] = React.useState(false);

  useEffect(() => {
    setIsMounted(true);
    if (isMounted) {
      onLoad();
    }

    return () => {
      setIsMounted(false);
    };
  }, [isMounted, onLoad]);

  return (
    <>
      {invoices.map((invoice) => {
        const {
          fixedLineItems, usageLineItems, commitmentLineItems, promotionLineItems, revisionedLineItems,
          customer, totalCommitment, notes, revenueBreakdown, billingCycle, purchaseOrder, id, currency,
          paymentDueDays, paymentTerm, paymentProviderInformation, billingStatus,
        } = invoice;

        const currencySymbol = getCurrencySymbol(currency);

        return (
          <div
            key={crypto.randomUUID()}
            id={`invoice-pdf-${id}`}
            className='min-h-a4 min-w-a4 container flex h-full flex-col gap-1 overflow-visible bg-white px-9 py-4'
          >
            {isStatement && (
              <LightInvoiceStatementHeader
                accountName={companyName}
                billingCycle={billingCycle}
                invoiceId={id}
              />
            )}
            <LightInvoiceHeader
              isStatement={isStatement}
              commitment={totalCommitment}
              companyName={companyName}
              customerName={customer.name}
              totalPrice={revenueBreakdown.total}
              billingCycle={billingCycle}
              purchaseOrder={purchaseOrder}
              invoiceId={id}
              currencySymbol={currencySymbol}
              paymentDueDays={paymentDueDays}
              paymentTerm={paymentTerm}
              paymentProviderInformation={paymentProviderInformation}
              billingStatus={billingStatus}
              customerAddress={customer.address}
            />
            {isNotEmptyArray(commitmentLineItems) && (
              <LightInvoiceCommitmentCharges
                {...{ commitmentLineItems }}
                isPdf
                currency={currency}
              />
            )}
            {isNotEmptyArray(fixedLineItems) && (
              <LightInvoiceFixedFeeCharges
                fixedLineItems={fixedLineItems}
                isPdf
                currency={currency}
              />
            )}
            {isNotEmptyArray(usageLineItems) && (
              <LightInvoiceUsageCharges
                {...{ usageLineItems }}
                isPdf
                currency={currency}
              />
            )}
            {isNotEmptyArray(promotionLineItems) && (
              <LightInvoicePromotions
                {...{ promotionLineItems }}
                isPdf
                chargedAmount={revenueBreakdown.total}
                promotionsAmount={revenueBreakdown.discount}
                currency={currency}
              />
            )}
            {isNotEmptyArray(revisionedLineItems) && (
              <LightInvoiceRevisionedLineItem
                {...{ revisionedLineItems }}
                isPdf
                currency={currency}
              />
            )}
            <div className="flex w-full flex-1 items-end">
              <LightInvoiceSummary
                revenueBreakdown={revenueBreakdown}
                currency={currency}
                isPdf
              />
            </div>
            <LightInvoiceFooter
              invoiceNotes={notes}
              isPdf
            />
          </div>
        );
      })}
    </>
  );
};